import * as React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import Grid from '@mui/material/Grid2';
import Legend from '../Legend';
import ProgressBar from '../progressBar/ProgressBar';
import { Charts } from '../../types/Dashboard';
import { formatNumber } from '../../utils/formatNumber';
import { DashboardCard } from '../Common/SurfaceCard';
import { ChartFlexContainer, ProgressBarSection } from './styledElements';

interface MonthlyEntriesProps {
    charts: Charts;
}

export default function MonthlyEntries({ charts }: MonthlyEntriesProps) {
    const { t } = useTranslation("common");
    const theme = useTheme();
    const fnPadDigits = (n: number, totalDigits: number) =>
        n.toString().padStart(totalDigits, "0");
    const entriesPerMonth = charts.totalEntriesPerMonth;

    const barGraphToday = entriesPerMonth.yValues.realValues.map(
        (realValue, index) =>
            realValue - entriesPerMonth.yValues.newCustomers[index]
    );

    const graphColors = {
        graphEstimatedColor: alpha(theme.palette.primary.dark, 0.4),
        graphBarColor: theme.palette.primary.dark,
        graphBarNewClientsColor: alpha(theme.palette.success.main, 0.6),
    };

    const progressBarsConfig = {
        month: [
            {
                current: charts.totalEntriesThisMonthLastMonthLastYearThisTime.thisMonth,
                max: charts.totalEntriesThisMonthLastMonthLastYearThisTime.max,
                color: theme.palette.primary.light,
            },
            {
                current: charts.totalEntriesThisMonthLastMonthLastYearThisTime.lastMonth,
                max: charts.totalEntriesThisMonthLastMonthLastYearThisTime.max,
                color: theme.palette.primary.main,
            },
            {
                current: charts.totalEntriesThisMonthLastMonthLastYearThisTime.lastYear,
                max: charts.totalEntriesThisMonthLastMonthLastYearThisTime.max,
                color: theme.palette.primary.dark,
            },
        ],
    };

    const titles = {
        month: [
            t("clients_this_month"),
            t("clients_last_month"),
            t("clients_last_year"),
        ],
    };

    return (
        <DashboardCard title={t("entries_per_month_title")}>
            <Grid container spacing={1.5} flexGrow={1}>
                <Grid size={{ xs: 12, md: 4 }} alignItems="center" display="flex" justifyContent="center">
                    <ProgressBarSection>
                        <ProgressBar
                            showPercentage={true}
                            showLegend={false}
                            progressBars={progressBarsConfig.month}
                            titleBars={titles.month}
                            sx={{ maxWidth: (theme) => theme.spacing(18), maxHeight: (theme) => theme.spacing(18) }}
                        />
                         <Legend
                            small
                            direction="row"
                            items={progressBarsConfig.month.map((bar, i) => ({
                                color: bar.color,
                                label: titles.month[i],
                            }))}
                        />
                    </ProgressBarSection>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }} alignItems="center" display="flex" flexDirection="column">
                    <Legend
                        items={[
                            { color: graphColors.graphBarNewClientsColor, label: t("barchart2_set3") },
                            { color: graphColors.graphBarColor, label: t("barchart2_set1") },
                            { color: graphColors.graphEstimatedColor, label: t("barchart2_set2") },
                        ]}
                    />
                    <ChartFlexContainer>
                        <Bar
                            data={{
                                labels: entriesPerMonth.xValues.map(
                                    (x) => `${fnPadDigits(x.month, 2)} / ${x.year}`
                                ),
                                datasets: [
                                    {
                                        label: t("barchart2_set3"),
                                        data: entriesPerMonth.yValues.newCustomers,
                                        backgroundColor: graphColors.graphBarNewClientsColor,
                                    },
                                    {
                                        label: t("barchart2_set1"),
                                        data: barGraphToday,
                                        backgroundColor: graphColors.graphBarColor,
                                    },
                                    {
                                        label: t("barchart2_set2"),
                                        data: entriesPerMonth.yValues.estimatedValues,
                                        backgroundColor: graphColors.graphEstimatedColor,
                                    },
                                ],
                            }}
                            style={{
                                width: "100%",
                                height: "100%",
                                maxWidth: "100%",
                                maxHeight: "100%",
                            }}
                            options={{
                                locale: "ro",
                                maintainAspectRatio: false,
                                scales: {
                                    y: {
                                        stacked: true,
                                        beginAtZero: true,
                                        ticks: { color: theme.palette.text.secondary },
                                        grid: { color: theme.palette.divider },
                                    },
                                    x: {
                                        stacked: true,
                                        ticks: { color: theme.palette.text.secondary },
                                        grid: { color: theme.palette.divider },
                                    },
                                },
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            label: () => "",
                                            footer: (tooltipItem) => {
                                                const total = formatNumber(
                                                    entriesPerMonth.yValues.estimatedValues[tooltipItem[0].dataIndex] +
                                                    entriesPerMonth.yValues.realValues[tooltipItem[0].dataIndex]
                                                );
                                                if (entriesPerMonth.yValues.estimatedValues[tooltipItem[0].dataIndex] === 0) {
                                                    return `Total: ${total} \nClienti noi: ${Math.round(
                                                        (entriesPerMonth.yValues.newCustomers[tooltipItem[0].dataIndex] /
                                                            entriesPerMonth.yValues.realValues[tooltipItem[0].dataIndex]) * 100
                                                    )}%`;
                                                } else {
                                                    return `Total: ${total} \nNew clients: ${Math.round(
                                                        (entriesPerMonth.yValues.newCustomers[tooltipItem[0].dataIndex] /
                                                            entriesPerMonth.yValues.realValues[tooltipItem[0].dataIndex]) * 100
                                                    )}%\nEstimated: ${entriesPerMonth.yValues.estimatedValues[tooltipItem[0].dataIndex]
                                                        .toString()
                                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
                                                }
                                            },
                                        },
                                    },
                                    legend: { display: false },
                                },
                            }}
                        />
                    </ChartFlexContainer>
                </Grid>
            </Grid>
        </DashboardCard>
    );
}
